<template>
  <a-form layout="vertical" :model="formModel">
    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.name ? 'error' : ''"
      :help="fieldErrors.name || ''"
    >
      <label for="name" class="form-label"
        >姓名<span class="required">*</span></label
      >
      <a-input
        id="name"
        class="custom-input-style"
        v-model:value="formModel.name"
        @input="() => clearFieldError('name')"
      />
    </a-form-item>

    <a-form-item class="custom-form-item custom-input">
      <label for="email" class="form-label">Email</label>
      <a-input
        id="email"
        class="custom-input-style"
        v-model:value="formModel.email"
        disabled
      />
    </a-form-item>

    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.phone ? 'error' : ''"
      :help="fieldErrors.phone || ''"
    >
      <label for="phone" class="form-label">電話</label>
      <div style="display: flex; gap: 8px">
        <a-input
          id="phone"
          class="custom-input-style"
          v-model:value="formModel.phone"
          @input="handlePhoneChange"
        />
        <a-button
          class="send-code-btn"
          :disabled="countdown > 0 || !formModel.phone || !isPhoneChanged"
          @click="sendSmsCode"
        >
          {{ countdown > 0 ? `${countdown} 秒後重發` : "發送驗證碼" }}
        </a-button>
      </div>
    </a-form-item>

    <a-form-item
      v-if="isPhoneChanged"
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.sms_code ? 'error' : ''"
      :help="fieldErrors.sms_code || ''"
    >
      <label for="sms_code" class="form-label"
        >簡訊認證碼<span class="required">*</span></label
      >
      <a-input
        id="sms_code"
        class="custom-input-style"
        v-model:value="formModel.sms_code"
        placeholder="請輸入您收到的簡訊驗證碼"
        @input="() => clearFieldError('sms_code')"
      />
    </a-form-item>

    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.organization ? 'error' : ''"
      :help="fieldErrors.organization || ''"
    >
      <label for="organization" class="form-label">所屬單位</label>
      <a-input
        id="organization"
        class="custom-input-style"
        v-model:value="formModel.organization"
        @input="() => clearFieldError('organization')"
      />
    </a-form-item>

    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.department ? 'error' : ''"
      :help="fieldErrors.department || ''"
    >
      <label for="department" class="form-label">部門</label>
      <a-input
        id="department"
        class="custom-input-style"
        v-model:value="formModel.department"
        @input="() => clearFieldError('department')"
      />
    </a-form-item>

    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.job_title ? 'error' : ''"
      :help="fieldErrors.job_title || ''"
    >
      <label for="job_title" class="form-label">職稱</label>
      <a-input
        id="job_title"
        class="custom-input-style"
        v-model:value="formModel.job_title"
        @input="() => clearFieldError('job_title')"
      />
    </a-form-item>

    <a-form-item
      class="custom-form-item custom-input"
      :validate-status="fieldErrors.id_number ? 'error' : ''"
      :help="fieldErrors.id_number || ''"
    >
      <label for="id_number" class="form-label">身分證字號</label>
      <a-input
        id="id_number"
        class="custom-input-style"
        v-model:value="formModel.id_number"
        @input="() => clearFieldError('id_number')"
      />
    </a-form-item>

    <a-form-item
      v-if="props.mode !== 'register' && formModel.api_key_enabled"
      class="custom-form-item custom-input"
      id="api-key-section"
    >
      <label class="form-label">API Key</label>
      <div class="api-key-row">
        <a-input class="custom-input-style" :value="maskedApiKey" readonly />
        <a-button class="api-key-toggle-btn" @click="showApiKey = !showApiKey">
          <EyeOutlined v-if="!showApiKey" />
          <EyeInvisibleOutlined v-else />
        </a-button>
        <a-button
          danger
          class="api-key-regenerate-btn"
          @click="confirmRegenerateApiKey"
        >
          重新生成
        </a-button>
      </div>
    </a-form-item>

    <div class="profile-form-actions">
      <a-button class="cancel-btn" @click="handleCancel">
        <span>{{ cancelText }}</span>
      </a-button>
      <a-button
        class="confirm-btn"
        type="primary"
        :loading="submitLoading"
        @click="handleSubmit"
      >
        <span>{{ submitText }}</span>
      </a-button>
    </div>
  </a-form>
</template>

<script setup>
import { computed, onMounted, onUnmounted, reactive, ref } from "vue";
import { useRouter } from "vue-router";
import { message, Modal } from "ant-design-vue";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons-vue";
import { authAPI } from "@/services/api";

const props = defineProps({
  mode: {
    type: String,
    default: "edit",
  },
});

const router = useRouter();
const submitLoading = ref(false);
const fieldErrors = reactive({});

const originalPhone = ref("");
const isPhoneChanged = ref(false);
const countdown = ref(0);
const countdownTimer = ref(null);
const showApiKey = ref(false);

const formModel = reactive({
  name: "",
  email: "",
  phone: "",
  sms_code: "",
  organization: "",
  department: "",
  job_title: "",
  id_number: "",
  api_key: "",
  api_key_enabled: false,
});

const submitText = computed(() =>
  props.mode === "register" ? "完成註冊" : "確認"
);
const cancelText = computed(() =>
  props.mode === "register" ? "返回" : "取消"
);
const maskedApiKey = computed(() => {
  const key = formModel.api_key || "";

  if (!key) return "";
  if (showApiKey.value) return key;

  return `${"*".repeat(Math.max(0, key.length - 8))}${key.slice(-8)}`;
});

const clearFieldError = (key) => {
  if (fieldErrors[key]) {
    delete fieldErrors[key];
  }
};

const handlePhoneChange = () => {
  clearFieldError("phone");
  isPhoneChanged.value = formModel.phone !== originalPhone.value;
};

const sendSmsCode = async () => {
  if (!formModel.phone) {
    message.warning("請先輸入手機號碼");
    return;
  }

  try {
    const response = await authAPI.resendMobileCode({ phone: formModel.phone });
    if (response.status >= 200 && response.status < 300) {
      message.success("驗證碼已發送");
      countdown.value = 60;
      countdownTimer.value = setInterval(() => {
        countdown.value--;
        if (countdown.value <= 0) {
          clearInterval(countdownTimer.value);
        }
      }, 1000);
    } else {
      message.error("發送驗證碼失敗，請稍後再試");
    }
  } catch (error) {
    const serverMessage = error?.response?.data?.message;
    message.error(serverMessage || "發送驗證碼失敗，請稍後再試");
  }
};

onUnmounted(() => {
  if (countdownTimer.value) clearInterval(countdownTimer.value);
});

const validateForm = () => {
  Object.keys(fieldErrors).forEach((key) => delete fieldErrors[key]);

  if (!formModel.name) {
    fieldErrors.name = "請輸入姓名";
  }

  if (formModel.phone && String(formModel.phone).length > 20) {
    fieldErrors.phone = "電話長度不可超過 20";
  }

  if (isPhoneChanged.value && !formModel.sms_code) {
    fieldErrors.sms_code = "請輸入簡訊認證碼";
  }

  if (formModel.id_number && String(formModel.id_number).length > 20) {
    fieldErrors.id_number = "身分證字號長度不可超過 20";
  }

  return Object.keys(fieldErrors).length === 0;
};

const fetchUserInfo = async () => {
  const response = await authAPI.me();
  const user = response.data || {};
  formModel.name = user.name || "";
  formModel.email = user.email || "";
  formModel.phone = user.phone || "";
  originalPhone.value = user.phone || "";
  formModel.organization = user.organization || "";
  formModel.department = user.department || "";
  formModel.job_title = user.job_title || "";
  formModel.id_number = user.id_number || "";
  formModel.api_key = user.api_key || "";
  formModel.api_key_enabled = !!user.api_key_enabled;
};

const confirmRegenerateApiKey = () => {
  Modal.confirm({
    title: "確定要重新生成 API Key 嗎？",
    content: "重新生成後舊的 API Key 會立即失效。",
    okText: "確認重新生成",
    cancelText: "取消",
    async onOk() {
      const response = await authAPI.regenerateApiKey();
      formModel.api_key = response.data?.api_key || "";
      showApiKey.value = true;
      message.success("API Key 已重新生成");
    },
  });
};

const clearRegistrationSessionStorage = () => {
  const itemsToRemove = [
    "emailVerifyMail",
    "emailVerifyToken",
    "registerName",
    "registerSource",
    "registerStepCompleted",
    "registerIdNumber",
    "registerAddress",
    "registerNationality",
    "registerEmail",
  ];

  itemsToRemove.forEach((item) => {
    sessionStorage.removeItem(item);
  });
};

const handleCancel = () => {
  if (props.mode === "register") {
    router.push("/");
    return;
  }
  router.push("/Dashboard");
};

const handleSubmit = async () => {
  if (!validateForm()) {
    message.error("請確認表單欄位");
    return;
  }

  try {
    submitLoading.value = true;

    if (isPhoneChanged.value) {
      try {
        const verifyResponse = await authAPI.verifyMobileCode({
          phone: formModel.phone,
          code: formModel.sms_code,
        });
        if (verifyResponse.status < 200 || verifyResponse.status >= 300) {
          message.error("驗證碼錯誤或手機號碼不符");
          submitLoading.value = false;
          return;
        }
      } catch (error) {
        const serverMessage = error?.response?.data?.message;
        message.error(serverMessage || "驗證碼錯誤或手機號碼不符");
        submitLoading.value = false;
        return;
      }
    }

    const response = await authAPI.updateProfile({
      name: formModel.name,
      phone: formModel.phone || null,
      organization: formModel.organization || null,
      department: formModel.department || null,
      job_title: formModel.job_title || null,
      id_number: formModel.id_number || null,
    });

    if (response.status >= 200 && response.status < 300) {
      localStorage.setItem("user_data", JSON.stringify(response.data || {}));

      if (props.mode === "register") {
        clearRegistrationSessionStorage();
        router.push("/successed");
        return;
      }

      originalPhone.value = formModel.phone;
      isPhoneChanged.value = false;
      formModel.sms_code = "";

      message.success("資料更新成功！");
      return;
    }

    message.error("更新失敗，請稍後再試");
  } catch (error) {
    const serverMessage = error?.response?.data?.message;
    const errors = error?.response?.data?.errors;

    if (errors && typeof errors === "object") {
      Object.keys(errors).forEach((key) => {
        const messages = errors[key];
        if (Array.isArray(messages) && messages.length > 0) {
          fieldErrors[key] = messages[0];
        }
      });
      message.error("請確認表單欄位");
      return;
    }

    message.error(serverMessage || "更新失敗，請稍後再試");
  } finally {
    submitLoading.value = false;
  }
};

onMounted(async () => {
  try {
    await fetchUserInfo();
  } catch (error) {
    if (error?.response?.status === 401) {
      localStorage.removeItem("ms_platform_token");
      router.push("/sign-in");
      return;
    }
    message.error("獲取用戶資訊失敗");
  }
});
</script>

<style scoped>
.custom-input-style {
  height: 56px;
  border-radius: 20px;
  padding: 0 16px;
  font-size: 16px;
  color: #000;
  width: 100%;
  box-sizing: border-box;
}

.custom-input-style:focus {
  border-color: #707070;
  box-shadow: none;
}

.custom-input-style::placeholder {
  color: #9c9c9c;
  opacity: 1;
}

.custom-form-item :deep(.ant-form-item-label > label) {
  font-size: 16px;
  font-weight: 500;
  color: #696969;
}

.profile-form-actions {
  width: 100%;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-top: 24px;
}

.cancel-btn {
  width: 100%;
  height: 56px;
  border-radius: 999px;
  font-size: 16px;
}

.confirm-btn {
  width: 100%;
  height: 56px;
  border-radius: 999px;
  font-size: 16px;
}

.required {
  color: #ff4d4f;
  margin-left: 4px;
}

.send-code-btn {
  height: 56px;
  border-radius: 20px;
  background-color: var(--color-primary, #022964);
  color: #ffffff;
  font-size: 16px;
  font-weight: 500;
  min-width: 120px;
}

.send-code-btn:disabled {
  background-color: #d9d9d9;
  color: #00000040;
  border-color: #d9d9d9;
}

.api-key-row {
  display: flex;
  gap: 8px;
  align-items: center;
}

.api-key-toggle-btn {
  width: 56px;
  height: 56px;
  border-radius: 20px;
}

.api-key-regenerate-btn {
  height: 56px;
  border-radius: 20px;
  padding: 0 24px;
  font-size: 16px;
  font-weight: 500;
  min-width: 120px;
}
</style>
